'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
    Shield, 
    Clock, 
    Coffee, 
    Wine, 
    Cigarette, 
    Brush, 
    Calendar, 
    Sparkles,
    CheckCircle,
    X,
    Download,
    Heart
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function MaintenanceSection() {
    const { t, isLoading } = useTranslation('teeth-whitening');
    const { isRTL } = useRTL();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const tipsRef = useRef<HTMLDivElement>(null);
    const timelineRef = useRef<HTMLDivElement>(null);
    const guideRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isLoading) return;

        const ctx = gsap.context(() => {
            // Title animation
            if (titleRef.current) {
                gsap.fromTo(titleRef.current,
                    { opacity: 0, y: 50 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: titleRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Tips animation
            if (tipsRef.current?.children) {
                const tips = Array.from(tipsRef.current.children);
                gsap.fromTo(tips,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        stagger: 0.1,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: tipsRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Timeline animation
            if (timelineRef.current) {
                gsap.fromTo(timelineRef.current,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: timelineRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Guide animation
            if (guideRef.current) {
                gsap.fromTo(guideRef.current,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: guideRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

        }, sectionRef);

        return () => ctx.revert();
    }, [isLoading]);

    const maintenanceTips = [
        {
            icon: Brush,
            title: t('maintenance.tips.oral_hygiene.title'),
            description: t('maintenance.tips.oral_hygiene.description'),
            type: 'do'
        },
        {
            icon: Coffee,
            title: t('maintenance.tips.avoid_staining.title'),
            description: t('maintenance.tips.avoid_staining.description'),
            type: 'avoid'
        },
        {
            icon: Calendar,
            title: t('maintenance.tips.regular_checkups.title'),
            description: t('maintenance.tips.regular_checkups.description'),
            type: 'do'
        },
        {
            icon: Cigarette,
            title: t('maintenance.tips.no_smoking.title'),
            description: t('maintenance.tips.no_smoking.description'),
            type: 'avoid'
        },
        {
            icon: Sparkles,
            title: t('maintenance.tips.whitening_toothpaste.title'),
            description: t('maintenance.tips.whitening_toothpaste.description'),
            type: 'do'
        },
        {
            icon: Wine,
            title: t('maintenance.tips.limit_staining_drinks.title'),
            description: t('maintenance.tips.limit_staining_drinks.description'),
            type: 'avoid'
        }
    ];

    const timelineData = [
        {
            period: t('maintenance.timeline.immediate.period'),
            title: t('maintenance.timeline.immediate.title'),
            description: t('maintenance.timeline.immediate.description'),
            icon: Shield
        },
        {
            period: t('maintenance.timeline.first_week.period'),
            title: t('maintenance.timeline.first_week.title'),
            description: t('maintenance.timeline.first_week.description'),
            icon: Heart
        },
        {
            period: t('maintenance.timeline.first_month.period'),
            title: t('maintenance.timeline.first_month.title'),
            description: t('maintenance.timeline.first_month.description'),
            icon: Sparkles
        },
        {
            period: t('maintenance.timeline.ongoing.period'),
            title: t('maintenance.timeline.ongoing.title'),
            description: t('maintenance.timeline.ongoing.description'),
            icon: Calendar
        }
    ];

    if (isLoading) {
        return (
            <section className="py-16 lg:py-24 bg-slate-50 dark:bg-slate-900">
                <div className="container">
                    <div className="text-center mb-16 animate-pulse">
                        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mb-6 max-w-4xl mx-auto"></div>
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded max-w-3xl mx-auto"></div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse"></div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section id="maintenance" ref={sectionRef} className="py-16 lg:py-24 bg-slate-50 dark:bg-slate-900">
            <div className="container">
                <div ref={titleRef} className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                        {t('maintenance.title.part1')} <span className="text-gold">{t('maintenance.title.part2')}</span>
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t('maintenance.subtitle')}
                    </p>
                </div>

                {/* Maintenance Tips */}
                <div ref={tipsRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16">
                    {maintenanceTips.map((tip, index) => (
                        <Card 
                            key={index} 
                            className={`border-2 transition-all duration-300 hover:shadow-lg ${
                                tip.type === 'do' 
                                    ? 'border-green-200 dark:border-green-800 hover:border-green-400' 
                                    : 'border-red-200 dark:border-red-800 hover:border-red-400'
                            } bg-white dark:bg-slate-800`}
                        >
                            <CardContent className="p-6">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                        tip.type === 'do' 
                                            ? 'bg-green-100 dark:bg-green-900/30' 
                                            : 'bg-red-100 dark:bg-red-900/30'
                                    }`}>
                                        <tip.icon className={`w-6 h-6 ${
                                            tip.type === 'do' 
                                                ? 'text-green-600 dark:text-green-400' 
                                                : 'text-red-600 dark:text-red-400'
                                        }`} />
                                    </div>
                                    {tip.type === 'do' ? (
                                        <CheckCircle className="w-6 h-6 text-green-500" />
                                    ) : (
                                        <X className="w-6 h-6 text-red-500" />
                                    )}
                                </div>
                                <h3 className="font-bold text-navy dark:text-white mb-3">
                                    {tip.title}
                                </h3>
                                <p className={`text-sm leading-relaxed ${isRTL ? 'text-right' : 'text-left'} text-slate-600 dark:text-slate-300`}>
                                    {tip.description}
                                </p>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Maintenance Timeline */}
                <div ref={timelineRef} className="max-w-4xl mx-auto mb-16">
                    <Card className="bg-gold/10 border-gold/30">
                        <CardContent className="p-8">
                            <h3 className="text-2xl font-bold text-navy dark:text-white mb-8 text-center">
                                {t('maintenance.timeline.title')}
                            </h3>

                            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {timelineData.map((item, index) => (
                                    <div key={index} className="text-center">
                                        <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <item.icon className="w-8 h-8 text-gold" />
                                        </div>
                                        <div className="text-sm font-semibold text-gold mb-2">
                                            {item.period}
                                        </div>
                                        <h4 className="font-bold text-navy dark:text-white mb-2">
                                            {item.title}
                                        </h4>
                                        <p className={`text-sm text-slate-600 dark:text-slate-300 ${isRTL ? 'text-right' : 'text-center'}`}>
                                            {item.description}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Downloadable Maintenance Guide */}
                <div ref={guideRef} className="max-w-4xl mx-auto">
                    <Card className="bg-gold/10 border-gold/30">
                        <CardContent className="p-8 text-center">
                            <div className={`flex items-center justify-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <Download className="w-8 h-8 text-gold" />
                                <h3 className="text-2xl font-bold text-navy dark:text-white">
                                    {t('maintenance.guide.title')}
                                </h3>
                            </div>
                            <p className={`text-slate-600 dark:text-slate-300 mb-6 leading-relaxed ${isRTL ? 'text-right' : 'text-center'}`}>
                                {t('maintenance.guide.description')}
                            </p>

                            <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                                <Button
                                    className={`bg-gold hover:bg-gold-600 text-navy px-6 py-3 font-semibold rounded-xl shadow-lg transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${isRTL ? 'flex-row-reverse' : ''}`}
                                    disabled
                                    title="Coming soon"
                                    aria-disabled="true"
                                >
                                    <Download className="w-5 h-5" />
                                    {t('maintenance.guide.download')}
                                </Button>

                                <p className={`text-sm text-slate-600 dark:text-slate-300 flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <span>{t('maintenance.guide.questions')}</span>
                                    <a
                                        href="#faq"
                                        className="text-gold hover:text-gold-600 font-semibold underline"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const faqSection = document.getElementById('faq');
                                            faqSection?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                    >
                                        {t('maintenance.guide.faq')}
                                    </a>
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </section>
    );
}